<template>
  <template v-if="budget && getBudgetPrices">
    <AnnualBudgetTotalCard data-cy="billing-mode-budget-total-card" />

    <div class="mt-8">
      <div class="items-center gap-4 md:flex" data-cy="billing-mode-budget-section">
        <RatioPieChartComponent
          :portion="getBudgetPercentages[0]"
          :total="100"
          :legend="`${getBudgetPercentages[0]}%`"
        />
        <div class="ml-3 mt-2 grow md:mt-0">
          <AtomText
            :text="t(`${translationPath}.energyPrice.title`)"
            font-style="font-medium"
            font-size="text-sm"
            data-cy="billing-mode-budget-section-title"
          />
          <ul class="mt-2 list-disc pl-4">
            <li>
              <div class="flex items-baseline justify-between gap-4">
                <div>
                  <AtomText
                    :text="t(`${translationPath}.energyPrice.content`)"
                    font-size="text-sm"
                    is-tag="span"
                  />&nbsp;<AtomText
                    :text="
                      t('subscription.billingMode.annualBudget.kWh', {
                        number: budget.energy.estimatedkWh,
                      })
                    "
                    font-size="text-sm"
                    font-style="font-medium"
                    is-tag="span"
                    data-cy="billing-mode-budget-energy-detail"
                  />
                </div>
                <AtomText
                  :text="getPriceText(getBudgetPrices.energyAnnual)"
                  font-style="font-medium"
                  font-size="text-sm"
                  data-cy="billing-mode-budget-energy-annual"
                />
              </div>
            </li>
            <AtomText
              :text="t(`${translationPath}.energyPrice.detail`)"
              font-size="text-xs"
              color-variant="description"
              class="-ml-4 mt-0.5 w-3/4"
            />
          </ul>
        </div>
      </div>

      <div class="mt-6 items-center gap-4 md:flex" data-cy="billing-mode-budget-section">
        <RatioPieChartComponent
          :portion="getBudgetPercentages[1]"
          :total="100"
          :legend="`${getBudgetPercentages[1]}%`"
        />
        <div class="ml-3 mt-2 grow md:mt-0">
          <AtomText
            :text="t(`${translationPath}.subscription.title`)"
            font-style="font-medium"
            font-size="text-sm"
            data-cy="billing-mode-budget-section-title"
          />
          <ul class="mt-2 list-disc pl-4">
            <li>
              <div class="flex items-baseline justify-between gap-4">
                <div>
                  <AtomText
                    :text="tm(`${translationPath}.subscription.content`)[0]"
                    font-size="text-sm"
                    is-tag="span"
                  />&nbsp;<AtomText
                    :text="`${getContractedPowerText(budget.subscription.kVA)}${getSubscriptionMonthlyText(getBudgetPrices.subscriptionMonthly)}`"
                    font-size="text-sm"
                    font-style="font-medium"
                    is-tag="span"
                    data-cy="billing-mode-budget-subscription-detail"
                  />
                </div>
                <AtomText
                  :text="getPriceText(getBudgetPrices.subscriptionAnnual)"
                  font-style="font-medium"
                  font-size="text-sm"
                  data-cy="billing-mode-budget-subscription-annual"
                />
              </div>
            </li>
            <li
              v-if="
                getBudgetPrices.subscriptionSelfContribution && (getHasSolarPanels || isMbSelected)
              "
            >
              <div class="flex items-baseline justify-between gap-4">
                <AtomText
                  :text="tm(`${translationPath}.subscription.content`)[1]"
                  font-size="text-sm"
                />
                <AtomText
                  :text="getPriceText(getBudgetPrices.subscriptionSelfContribution)"
                  font-style="font-medium"
                  font-size="text-sm"
                  data-cy="billing-mode-budget-subscription-self-contribution-annual"
                />
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="hasOptions"
        class="mt-6 items-center gap-4 md:flex"
        data-cy="billing-mode-budget-section"
      >
        <RatioPieChartComponent
          :portion="getBudgetPercentages[2]"
          :total="100"
          :legend="`${getBudgetPercentages[2]}%`"
        />
        <div class="ml-3 mt-2 grow md:mt-0">
          <AtomText
            :text="t(`${translationPath}.options.title`)"
            font-style="font-medium"
            font-size="text-sm"
            data-cy="billing-mode-budget-section-title"
          />
          <ul class="mt-2 list-disc pl-4">
            <li v-if="isMsbSelected && budget.options.msbOption">
              <div class="flex items-baseline justify-between gap-4">
                <div>
                  <AtomText
                    :text="tm(`${translationPath}.options.content`)[0]"
                    font-size="text-sm"
                    is-tag="span"
                  />&nbsp;<AtomText
                    :text="
                      getMsbMonthlyText(budget.options.msbOption.size, getBudgetPrices.msbMonthly)
                    "
                    font-size="text-sm"
                    font-style="font-medium"
                    is-tag="span"
                    data-cy="billing-mode-budget-msb-detail"
                  />
                </div>
                <AtomText
                  :text="getPriceText(getBudgetPrices.msbAnnual)"
                  font-style="font-medium"
                  font-size="text-sm"
                  data-cy="billing-mode-budget-msb-annual"
                />
              </div>
            </li>

            <li v-if="isMbSelected">
              <div class="flex items-baseline justify-between gap-4">
                <div>
                  <AtomText
                    :text="tm(`${translationPath}.options.content`)[1]"
                    font-size="text-sm"
                    is-tag="span"
                  />&nbsp;<AtomText
                    v-if="getBudgetPrices.mbMonthly"
                    :text="getMbMonthlyText(getBudgetPrices.mbMonthly)"
                    font-size="text-sm"
                    font-style="font-medium"
                    is-tag="span"
                    data-cy="billing-mode-budget-mb-detail"
                  />
                </div>
                <AtomText
                  :text="getPriceText(getBudgetPrices.mbAnnual)"
                  font-style="font-medium"
                  font-size="text-sm"
                  data-cy="billing-mode-budget-mb-annual"
                />
              </div>
            </li>
            <li v-if="encoreDiscount" data-cy="billing-mode-budget-encore">
              <div class="flex items-baseline justify-between gap-4">
                <AtomText
                  :text="
                    t(`${translationPath}.options.encoreDiscount`, {
                      value: formatEuroPrice(encoreDiscount.monthly),
                    })
                  "
                  font-size="text-sm"
                  data-cy="billing-mode-budget-encore-detail"
                />
                <AtomText
                  :text="
                    t('subscription.billingMode.annualBudget.discountPerYear', {
                      value: formatEuroPrice(encoreDiscount.annual),
                    })
                  "
                  font-style="font-medium"
                  font-size="text-sm"
                  data-cy="billing-mode-budget-encore-annual"
                />
              </div>
            </li>
            <li v-if="isPilotageSelected">
              <div class="flex items-baseline justify-between gap-4">
                <AtomText :text="tm(`${translationPath}.options.content`)[2]" font-size="text-sm" />
                <AtomText
                  :text="getPriceText(getBudgetPrices.pilotageAnnual)"
                  font-style="font-medium"
                  font-size="text-sm"
                  data-cy="billing-mode-budget-msc-annual"
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </template>
  <div class="mt-8 flex gap-1">
    <AtomText
      :text="tm('subscription.billingMode.annualBudget.modal.link')[0]"
      color-variant="body"
      font-size="text-sm"
    />
    <MoleculeStandaloneLink
      :label="tm('subscription.billingMode.annualBudget.modal.link')[1]"
      target="blank"
      type="url"
      font-size="sm"
      font-weight="medium"
      icon-name="ExternalLinkIcon"
      :to="pricingTableLink"
      color-variant="brand"
    />
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import useEncoreDiscount from '@/composables/useEncoreDiscount.composable';
import { useSubscriptionStore } from '@/stores';

import { computeBudgetPercentages } from '@/utils/budget.util';
import { formatEuroPrice } from '@/utils/format.util';

import RatioPieChartComponent from '@/components/RatioPieChart.component.vue';

import AnnualBudgetTotalCard from './AnnualBudgetTotalCard.component.vue';

const { t, tm } = useI18n();
const translationPath = 'subscription.billingMode.annualBudget.modal';

const subscriptionStore = useSubscriptionStore();
const {
  getBudgetBreakdown: budget,
  getBudgetPrices,
  isMsbSelected,
  isMbSelected,
  isPilotageSelected,
  getHasSolarPanels,
} = storeToRefs(subscriptionStore);

const { encoreDiscount } = useEncoreDiscount();

const getTotalPricePerSection = computed(() => {
  if (getBudgetPrices.value) {
    const {
      subscriptionAnnual,
      subscriptionSelfContribution,
      msbAnnual,
      pilotageAnnual,
      mbAnnual,
    } = getBudgetPrices.value;
    return {
      subscription: (subscriptionAnnual ?? 0) + (subscriptionSelfContribution ?? 0),
      options: msbAnnual + pilotageAnnual + mbAnnual - (encoreDiscount.value?.annual ?? 0),
    };
  }
  return { subscription: 0, options: 0 };
});

const getBudgetPercentages = computed(() =>
  computeBudgetPercentages(
    [
      getBudgetPrices.value?.energyAnnual ?? 0,
      getTotalPricePerSection.value.subscription,
      getTotalPricePerSection.value.options,
    ],
    getBudgetPrices.value?.totalAnnual ?? 0
  )
);

const hasOptions = computed(
  () => isMsbSelected.value || isMbSelected.value || isPilotageSelected.value
);

const getContractedPowerText = (kva: number) => {
  return t('subscription.billingMode.annualBudget.kVA', { number: kva });
};

const getSubscriptionMonthlyText = (subscriptionMonthly: number) => {
  if (!subscriptionMonthly) {
    return '';
  }

  return ` - ${getMonthlyPriceText(subscriptionMonthly)}`;
};

const getMbMonthlyText = (mbMonthly: number) => {
  if (!mbMonthly) {
    return '';
  }

  return `- ${getMonthlyPriceText(mbMonthly)}${t('subscription.billingMode.annualBudget.vat')}`;
};

const getMsbMonthlyText = (msbSize: number, msbMonthly: number) => {
  const text = t('subscription.billingMode.annualBudget.kWh', {
    number: msbSize,
  });

  if (!msbMonthly) {
    return text;
  }

  return `${text} - ${getMonthlyPriceText(msbMonthly)}`;
};

const getMonthlyPriceText = (price: number) => {
  return t('subscription.billingMode.annualBudget.pricePerMonth', {
    count: price,
    value: formatEuroPrice(price),
  });
};

const getPriceText = (price: number) => {
  return t('subscription.billingMode.annualBudget.price', {
    count: Math.ceil(price),
    value: formatEuroPrice(price),
  });
};

const pricingTableLink = t('common.link.priceList');
</script>
